import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Coins, Flame, ShoppingBag, Star, Gift, Lock, Trophy, Sparkles, Wrench, CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

/**
 * AdminOkonomi – Treasury-dashboard.
 *
 * Tre øverste kort: Reward Pool · Brent · I sirkulasjon (+ kontroll-likning)
 * Kategori-statkort: chip-kjøp · VIP-salg · interne kjøp
 * Manuell utdeling
 * Transaksjonslogg
 */

const STAT_THEMES = {
  green: { color: "var(--sage)",   icon: "bg-[var(--sage)]/15" },
  gold:  { color: "var(--gold)",   icon: "bg-[var(--gold)]/15" },
  magenta:{color: "var(--magenta)",icon: "bg-[var(--magenta)]/15" },
  cream: { color: "var(--cream)",  icon: "bg-white/5" },
};

function StatCard({ label, primary, secondary, sub, icon: Icon, theme = "cream", testId }) {
  const t = STAT_THEMES[theme];
  return (
    <div className="border border-white/10 bg-[var(--noir-elev)] p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="font-meta text-[10px] text-[var(--cream-faint)]">{label}</div>
        <div className={`p-2 ${t.icon}`}>
          <Icon className="h-4 w-4" style={{ color: t.color }} />
        </div>
      </div>
      <div className="font-fraunces italic text-3xl text-[var(--cream)] leading-none">
        {primary}
      </div>
      {secondary && (
        <div className="font-manrope text-sm text-[var(--cream-soft)] mt-2">{secondary}</div>
      )}
      {sub && (
        <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">{sub}</div>
      )}
    </div>
  );
}

const CAT_ICONS = {
  chip_purchase:        ShoppingBag,
  chip_purchase_cut:    Coins,
  vip_purchase:         Star,
  creator_vip_purchase: Star,
  vip_gift:             Gift,
  ppv_cut:              Lock,
  ppv_unlock:           Lock,
  feature_boost:        Sparkles,
  contest_prize:        Trophy,
  idea_bonus:           Sparkles,
  compensation:         Wrench,
  campaign:             Sparkles,
  welcome:              Star,
  other:                Coins,
  initial_balance:      Coins,
  historic_balance_seed:Coins,
  promo_redemption:     Gift,
};

const CAT_LABELS = {
  chip_purchase:        "Chip-kjøp",
  chip_purchase_cut:    "Reward pool inn (10%)",
  vip_purchase:         "VIP-kjøp",
  creator_vip_purchase: "Skaper-VIP-kjøp",
  vip_gift:             "VIP-gave",
  ppv_cut:              "PPV admin-cut",
  ppv_unlock:           "PPV-låsing",
  feature_boost:        "Fremheving",
  contest_prize:        "Konkurranse-premie",
  idea_bonus:           "Idé-bonus",
  compensation:         "Kompensasjon",
  campaign:             "Kampanje",
  welcome:              "Velkomst-bonus",
  other:                "Annet",
  initial_balance:      "Initial saldo",
  historic_balance_seed:"Historisk justering",
  promo_redemption:     "Promo-kode",
};

export default function AdminOkonomi() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [treasury, setTreasury] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [trend, setTrend] = useState([]);
  const [log, setLog] = useState([]);
  const [grantCategories, setGrantCategories] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  // Grant form
  const [grantUser, setGrantUser] = useState("");
  const [grantChips, setGrantChips] = useState(50);
  const [grantCategory, setGrantCategory] = useState("idea_bonus");
  const [grantNote, setGrantNote] = useState("");
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, b, tr, l, gc] = await Promise.all([
        api.get("/admin/treasury"),
        api.get(`/admin/treasury/breakdown?days=${period}`),
        api.get("/admin/treasury/trend?weeks=12"),
        api.get(`/admin/treasury/log?days=${period}&per_page=50`),
        api.get("/admin/treasury/grant-categories"),
      ]);
      setTreasury(t.data);
      setBreakdown(b.data);
      setTrend(tr.data.weeks || []);
      setLog(l.data.results || []);
      setGrantCategories(gc.data.categories || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke hente treasury");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (!["admin", "owner"].includes(user.role)) { nav("/"); return; }
    load();
  }, [user, authLoading, nav, load]);

  const submitGrant = async (e) => {
    e.preventDefault();
    if (!grantUser || grantChips < 1) {
      toast.error("Fyll inn brukernavn og positiv chip-mengde");
      return;
    }
    setGranting(true);
    try {
      await api.post("/admin/treasury/grant", {
        username: grantUser, chips: Number(grantChips),
        category: grantCategory, note: grantNote,
      });
      toast.success(`${grantChips} chips utdelt til ${grantUser}`);
      setGrantUser(""); setGrantNote(""); setGrantChips(50);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke utbetale");
    } finally {
      setGranting(false);
    }
  };

  if (loading || !treasury) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-16 font-meta text-[var(--cream-soft)]">
          Henter treasury-data …
        </div>
      </Layout>
    );
  }

  const balanced = treasury.check_equation?.balanced;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-10 px-4" data-testid="admin-okonomi-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="font-meta text-[var(--magenta)] mb-3">ADMIN · CHIP-ØKONOMI</div>
            <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-none">
              Treasury.
            </h1>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mt-3 max-w-2xl">
              Innsyn i hver eneste chip på plattformen. Reward Pool brukes til konkurransepremier,
              idé-bonuser og kompensasjon. 10% av PPV-låsinger (bilde/film/novelle) fylles automatisk på — creator får 90%.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select value={period} onChange={(e) => setPeriod(Number(e.target.value))}
                    className="bg-[var(--noir-elev)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/40 outline-none font-meta"
                    data-testid="period-select">
              <option value={7}>Siste 7 dager</option>
              <option value={30}>Siste 30 dager</option>
              <option value={90}>Siste 90 dager</option>
              <option value={365}>Siste år</option>
            </select>
          </div>
        </div>

        {/* Hovedsaldoer */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="REWARD POOL" theme="green" icon={Coins} testId="stat-reward-pool"
            primary={treasury.reward_pool.toLocaleString("nb-NO")}
            secondary="chips tilgjengelig for utdeling"
            sub={`Auto-påfylling: 10% av chip-kjøp`}
          />
          <StatCard
            label="BRENT TOTALT" theme="magenta" icon={Flame} testId="stat-burned"
            primary={treasury.burn_total.toLocaleString("nb-NO")}
            secondary="chips forsvunnet fra økonomien"
            sub="VIP-kjøp + 10% cuts"
          />
          <StatCard
            label="I SIRKULASJON" theme="gold" icon={ShoppingBag} testId="stat-circulation"
            primary={treasury.in_circulation.toLocaleString("nb-NO")}
            secondary="chips på bruker-balanser"
            sub={`${treasury.active_users_30d} aktive brukere`}
          />
          <StatCard
            label="LIVSTID MINTET" theme="cream" icon={Sparkles} testId="stat-lifetime"
            primary={treasury.lifetime_minted.toLocaleString("nb-NO")}
            secondary="chips skapt totalt"
            sub={balanced ? "✓ regnskap balansert" : "⚠ ubalansert"}
          />
        </div>

        {/* Kontroll-likning */}
        <div className={`border px-5 py-3 mb-10 flex items-center gap-3 ${balanced ? "border-[var(--sage)]/40 bg-[var(--sage)]/8" : "border-[var(--magenta)]/40 bg-[var(--magenta)]/10"}`}
             data-testid="check-equation">
          {balanced ? <CheckCircle2 className="h-5 w-5 text-[var(--sage)]" /> : <AlertTriangle className="h-5 w-5 text-[var(--magenta)]" />}
          <div className="font-manrope text-sm text-[var(--cream)]">
            <span className="font-meta text-[var(--cream-soft)]">REGNSKAP:</span>{" "}
            sirkulasjon ({treasury.in_circulation.toLocaleString("nb-NO")})
            + reward pool ({treasury.reward_pool.toLocaleString("nb-NO")})
            + brent ({treasury.burn_total.toLocaleString("nb-NO")})
            = <strong>{treasury.check_equation.expected.toLocaleString("nb-NO")}</strong>
            {balanced ? " stemmer med " : " mot "}
            mintet ({treasury.lifetime_minted.toLocaleString("nb-NO")})
          </div>
        </div>

        {/* Kategori-fordeling */}
        <div className="mb-10">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">
            Aktivitet siste {breakdown?.period_days} dager
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="CHIPS KJØPT" theme="green" icon={ShoppingBag} testId="cat-chip-purchases"
              primary={breakdown.chip_purchases.chips.toLocaleString("nb-NO")}
              secondary={`${breakdown.chip_purchases.nok.toFixed(0)} NOK totalt`}
              sub={`${breakdown.chip_purchases.count} transaksjoner · 10% (${breakdown.reward_pool_in.chips}) til pool`}
            />
            <StatCard
              label="VIP KJØPT" theme="gold" icon={Star} testId="cat-vip-purchases"
              primary={(breakdown.vip_purchases.chips + breakdown.vip_gifts.chips).toLocaleString("nb-NO")}
              secondary="chips brent"
              sub={`${breakdown.vip_purchases.count} egne + ${breakdown.vip_gifts.count} gave`}
            />
            <StatCard
              label="SKAPER-VIP KJØPT" theme="gold" icon={Star} testId="cat-creator-vip-purchases"
              primary={(breakdown.creator_vip_purchases?.chips || 0).toLocaleString("nb-NO")}
              secondary="chips brukt"
              sub={`${breakdown.creator_vip_purchases?.count || 0} kjøp`}
            />
            <StatCard
              label="INTERNE KJØP" theme="cream" icon={Lock} testId="cat-internal"
              primary={(breakdown.ppv_cuts.chips + breakdown.feature_boost.chips).toLocaleString("nb-NO")}
              secondary="chips i cut + fremheving"
              sub={`${breakdown.ppv_cuts.count} PPV · ${breakdown.feature_boost.count} fremheving`}
            />
          </div>
        </div>

        {/* Trend-graf siste 12 uker */}
        <div className="border border-white/10 bg-[var(--noir-elev)] p-5 mb-10" data-testid="treasury-trend">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Trend siste 12 uker</h2>
            <div className="flex gap-4 text-[10px] font-meta">
              <Legend />
            </div>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,235,224,0.06)" vertical={false} />
                <XAxis
                  dataKey="week"
                  stroke="rgba(242,235,224,0.5)"
                  tick={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}
                  tickFormatter={(v) => v.split("-W")[1]}
                />
                <YAxis stroke="rgba(242,235,224,0.5)" tick={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--noir)",
                    border: "1px solid rgba(242,235,224,0.15)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11,
                  }}
                  labelStyle={{ color: "var(--cream)" }}
                  formatter={(v, n) => [`${v} chips`, n]}
                />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", paddingTop: 8 }} />
                <Line type="monotone" dataKey="purchased" name="Kjøpt" stroke="var(--sage)"    strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="pool_in"   name="Til pool" stroke="var(--gold)"   strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="paid_out"  name="Utdelt"  stroke="var(--magenta)" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="burned"    name="Brent"   stroke="var(--cognac)"  strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-3 leading-relaxed">
            <strong className="text-[var(--sage)]">Kjøpt</strong> – nye chips fra ekte penger ·
            <strong className="text-[var(--gold)]"> Til pool</strong> – 10% PPV-cut ·
            <strong className="text-[var(--magenta)]"> Utdelt</strong> – konkurranse-premier og admin-bonuser ·
            <strong className="text-[var(--cognac)]"> Brent</strong> – VIP-kjøp og fremheving
          </div>
        </div>

        {/* Utdelinger */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* Utdelt sum */}
          <div className="border border-white/10 bg-[var(--noir-elev)] p-5">
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-3">UTDELT FRA REWARD POOL</div>
            <div className="font-fraunces italic text-3xl text-[var(--magenta)] mb-2">
              {(breakdown.contest_prizes.chips + breakdown.admin_grants.chips).toLocaleString("nb-NO")}
            </div>
            <div className="text-sm text-[var(--cream-soft)] space-y-1">
              <div>🏆 Konkurranse-premier: {breakdown.contest_prizes.chips} chips ({breakdown.contest_prizes.count}x)</div>
              <div>✨ Admin-utdelinger: {breakdown.admin_grants.chips} chips ({breakdown.admin_grants.count}x)</div>
            </div>
          </div>

          {/* Manuell utdeling */}
          <div className="lg:col-span-2 border border-white/10 bg-[var(--noir-elev)] p-5">
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-3">MANUELL UTDELING</div>
            <form onSubmit={submitGrant} className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <input
                value={grantUser} onChange={(e) => setGrantUser(e.target.value)}
                placeholder="Brukernavn" data-testid="grant-username"
                className="md:col-span-3 bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/40 outline-none"
              />
              <input
                type="number" min="1" max="10000"
                value={grantChips} onChange={(e) => setGrantChips(e.target.value)}
                data-testid="grant-chips"
                className="md:col-span-2 bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/40 outline-none"
              />
              <select
                value={grantCategory} onChange={(e) => setGrantCategory(e.target.value)}
                data-testid="grant-category"
                className="md:col-span-3 bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/40 outline-none font-meta text-xs"
              >
                {grantCategories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <input
                value={grantNote} onChange={(e) => setGrantNote(e.target.value)}
                placeholder="Notat (frivillig)" data-testid="grant-note"
                className="md:col-span-3 bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/40 outline-none"
              />
              <button
                type="submit" disabled={granting} data-testid="grant-submit"
                className="md:col-span-1 bg-[var(--magenta)] hover:bg-[var(--magenta-deep)] disabled:opacity-40 disabled:cursor-not-allowed text-black font-meta px-3 py-2 transition-colors"
              >
                {granting ? "…" : "GI"}
              </button>
            </form>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-3">
              ⚠ Trekkes fra reward pool ({treasury.reward_pool.toLocaleString("nb-NO")} tilgjengelig).
              Mottaker får varsel med kategori og notat.
            </div>
          </div>
        </div>

        {/* Transaksjonslogg */}
        <div className="border border-white/10 bg-[var(--noir-elev)]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-fraunces italic text-xl text-[var(--cream)]">Transaksjonslogg</h3>
            <div className="font-meta text-[10px] text-[var(--cream-faint)]">{log.length} siste</div>
          </div>
          <div className="max-h-[600px] overflow-y-auto" data-testid="treasury-log">
            {log.length === 0 ? (
              <div className="px-5 py-10 text-center font-meta text-[var(--cream-faint)]">
                Ingen transaksjoner i valgte periode.
              </div>
            ) : log.map((row) => {
              const Icon = CAT_ICONS[row.category] || Coins;
              const label = CAT_LABELS[row.category] || row.category;
              const isPositive = row.type === "mint" || row.type === "reward_pool_in";
              return (
                <div key={row.id} className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-4 hover:bg-white/[0.02] transition-colors" data-testid={`log-${row.id}`}>
                  <div className="shrink-0 p-2 bg-white/5">
                    <Icon className="h-4 w-4 text-[var(--cream-soft)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-fraunces italic text-sm text-[var(--cream)]">{label}</div>
                    <div className="font-manrope text-xs text-[var(--cream-faint)] truncate">
                      {row.note || "–"}
                      {row.user && <> · {row.user.username}</>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-fraunces italic ${isPositive ? "text-[var(--sage)]" : "text-[var(--magenta)]"}`}>
                      {isPositive ? "+" : "−"}{row.amount}
                    </div>
                    <div className="font-meta text-[10px] text-[var(--cream-faint)]">
                      {new Date(row.created_at).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <ChevronRight className="h-3 w-3 text-[var(--cream-faint)] shrink-0" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
